interface QuoteElements {
    prevButton: HTMLButtonElement;
    nextButton: HTMLButtonElement;
    currentElement: HTMLElement;
    totalElement: HTMLElement;
    quotesContainer: HTMLElement;
    quoteItems: HTMLElement[];
}

interface QuoteState {
    currentIndex: number;
    isScrolling: boolean;
    isAnimating: boolean;
    scrollTimeout?: number;
}

export const initQuotes = (): void => {
    const quoteBlocks = document.querySelectorAll<HTMLElement>(
        '[data-selector="quotes-block"]',
    );

    quoteBlocks.forEach((block) => {
        const quoteItems = Array.from(
            block.querySelectorAll<HTMLElement>("[data-quote-item]"),
        );

        if (quoteItems.length <= 1) return;

        const elements = getQuoteElements(block, quoteItems);
        if (!elements) return;

        const state: QuoteState = {
            currentIndex: 0,
            isScrolling: true,
            isAnimating: false,
            scrollTimeout: undefined,
        };

        const { currentElement, totalElement } = elements;

        totalElement.textContent = quoteItems.length.toString();
        currentElement.textContent = "1";

        const styleId = `quote-slider-styles-${Math.random().toString(36).substring(2, 9)}`;
        createAndInjectCSS(styleId);

        prepareQuotes(elements, state);
        startScrolling(elements, state);

        setupEventListeners(block, elements, state);
        setupResizeListener(elements, state);
    });
};

function getQuoteElements(
    block: HTMLElement,
    quoteItems: HTMLElement[],
): QuoteElements | null {
    const prevButton = block.querySelector<HTMLButtonElement>(
        '[data-selector="quotes-prev"]',
    );
    const nextButton = block.querySelector<HTMLButtonElement>(
        '[data-selector="quotes-next"]',
    );
    const currentElement = block.querySelector<HTMLElement>(
        '[data-selector="quotes-current"]',
    );
    const totalElement = block.querySelector<HTMLElement>(
        '[data-selector="quotes-total"]',
    );
    const quotesContainer = block.querySelector<HTMLElement>(
        '[data-selector="quotes-container"]',
    );

    if (
        !prevButton ||
        !nextButton ||
        !currentElement ||
        !totalElement ||
        !quotesContainer
    ) {
        console.error("Missing quote controls");
        return null;
    }

    return {
        prevButton,
        nextButton,
        currentElement,
        totalElement,
        quotesContainer,
        quoteItems,
    };
}

function setupEventListeners(
    block: HTMLElement,
    elements: QuoteElements,
    state: QuoteState,
): void {
    const { prevButton, nextButton } = elements;

    prevButton.addEventListener("click", () => {
        pauseScrolling(state);
        showPreviousQuote(elements, state);
    });

    nextButton.addEventListener("click", () => {
        pauseScrolling(state);
        showNextQuote(elements, state);
    });

    document.addEventListener("keydown", (event) => {
        const rect = block.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

        if (!isInViewport) return;

        if (event.key === "ArrowLeft") {
            pauseScrolling(state);
            showPreviousQuote(elements, state);
            event.preventDefault();
        } else if (event.key === "ArrowRight") {
            pauseScrolling(state);
            showNextQuote(elements, state);
            event.preventDefault();
        } else if (event.key === " " || event.key === "Spacebar") {
            toggleScrolling(elements, state);
            event.preventDefault();
        }
    });
}

function createAndInjectCSS(id: string): void {
    if (document.getElementById(id)) return;

    const styleElement = document.createElement("style");
    styleElement.id = id;
    styleElement.textContent = `
        .quotes-container {
            position: relative;
            overflow: hidden;
        }
        .quotes-wrapper {
            position: relative;
            min-height: 150px;
        }
        [data-quote-item] {
            transition: opacity 0.5s ease;
        }
        [data-quote-item].active {
            position: relative;
            z-index: 1;
            opacity: 1;
        }
        [data-quote-item].hidden {
            display: none;
            position: absolute;
            visibility: hidden;
        }
        [data-quote-item].staging {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            opacity: 0;
            display: block;
            z-index: 0;
        }
        [data-quote-item].fade-out {
            opacity: 0;
        }
        .quote-text {
            transition: transform 1s ease;
            transform: translateX(0);
        }
        .slide-left {
            transform: translateX(-100%);
        }
        .slide-right {
            transform: translateX(100%);
        }
    `;
    document.head.appendChild(styleElement);
}

function prepareQuotes(elements: QuoteElements, state: QuoteState): void {
    const { quoteItems } = elements;
    const { currentIndex } = state;

    quoteItems.forEach((quoteItem, index) => {
        const grid = quoteItem.querySelector<HTMLElement>(".grid");
        if (!grid) return;

        const quoteColumn = grid.querySelector<HTMLElement>(
            ":scope > div:last-child",
        );

        if (quoteColumn) {
            quoteColumn.classList.add("quote-text");
        }

        if (index === currentIndex) {
            quoteItem.classList.add("active");
        } else {
            quoteItem.classList.add("hidden");
        }

        quoteItem.setAttribute(
            "aria-hidden",
            index === currentIndex ? "false" : "true",
        );
    });

    setContainerHeight(elements, state);
}

function setContainerHeight(elements: QuoteElements, state: QuoteState): void {
    const { quoteItems } = elements;
    const { currentIndex } = state;

    const wrapper =
        elements.quotesContainer.querySelector<HTMLElement>(".quotes-wrapper");
    if (!wrapper) return;

    quoteItems.forEach((item) => {
        item.classList.remove("hidden");
        item.style.opacity = "0";
        item.style.position = "absolute";
        item.style.visibility = "hidden";
        item.style.display = "block";
    });

    let maxHeight = 0;
    quoteItems.forEach((item) => {
        const height = item.offsetHeight;
        maxHeight = Math.max(maxHeight, height);
    });

    wrapper.style.height = `${maxHeight}px`;

    quoteItems.forEach((item, index) => {
        const isActive = index === currentIndex;

        item.classList.toggle("active", isActive);
        item.classList.toggle("hidden", !isActive);

        item.style.removeProperty("opacity");
        item.style.removeProperty("position");
        item.style.removeProperty("visibility");
        item.style.removeProperty("display");
    });
}

function showNextQuote(elements: QuoteElements, state: QuoteState): void {
    if (state.isAnimating) return;
    state.isAnimating = true;

    const { quoteItems, currentElement } = elements;
    const oldIndex = state.currentIndex;

    state.currentIndex = (state.currentIndex + 1) % quoteItems.length;
    if (currentElement) {
        currentElement.textContent = (state.currentIndex + 1).toString();
    }

    const currentItem = quoteItems[oldIndex];
    const nextItem = quoteItems[state.currentIndex];

    const currentQuoteText =
        currentItem.querySelector<HTMLElement>(".quote-text");
    const nextQuoteText = nextItem.querySelector<HTMLElement>(".quote-text");

    nextItem.classList.remove("hidden");
    nextItem.classList.add("staging");
    nextItem.classList.add("fade-out");
    if (nextQuoteText) nextQuoteText.classList.add("slide-right");

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            currentItem.classList.add("fade-out");
            if (currentQuoteText) currentQuoteText.classList.add("slide-left");

            nextItem.classList.remove("fade-out");
            if (nextQuoteText) nextQuoteText.classList.remove("slide-right");

            currentItem.setAttribute("aria-hidden", "true");
            nextItem.setAttribute("aria-hidden", "false");

            setTimeout(() => {
                currentItem.classList.remove("active");
                nextItem.classList.remove("staging");
                nextItem.classList.add("active");

                currentItem.classList.add("hidden");
                currentItem.classList.remove("fade-out");
                if (currentQuoteText)
                    currentQuoteText.classList.remove("slide-left");

                state.isAnimating = false;
            }, 500);
        });
    });
}

function showPreviousQuote(elements: QuoteElements, state: QuoteState): void {
    if (state.isAnimating) return;
    state.isAnimating = true;

    const { quoteItems, currentElement } = elements;
    const oldIndex = state.currentIndex;

    state.currentIndex =
        (state.currentIndex - 1 + quoteItems.length) % quoteItems.length;
    if (currentElement) {
        currentElement.textContent = (state.currentIndex + 1).toString();
    }

    const currentItem = quoteItems[oldIndex];
    const prevItem = quoteItems[state.currentIndex];

    const currentQuoteText =
        currentItem.querySelector<HTMLElement>(".quote-text");
    const prevQuoteText = prevItem.querySelector<HTMLElement>(".quote-text");

    prevItem.classList.remove("hidden");
    prevItem.classList.add("staging");
    prevItem.classList.add("fade-out");
    if (prevQuoteText) prevQuoteText.classList.add("slide-left");

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            currentItem.classList.add("fade-out");
            if (currentQuoteText) currentQuoteText.classList.add("slide-right");

            prevItem.classList.remove("fade-out");
            if (prevQuoteText) prevQuoteText.classList.remove("slide-left");

            currentItem.setAttribute("aria-hidden", "true");
            prevItem.setAttribute("aria-hidden", "false");

            setTimeout(() => {
                currentItem.classList.remove("active");
                prevItem.classList.add("active");
                prevItem.classList.remove("staging");

                currentItem.classList.add("hidden");
                currentItem.classList.remove("fade-out");
                if (currentQuoteText)
                    currentQuoteText.classList.remove("slide-right");

                state.isAnimating = false;
            }, 500);
        });
    });
}

function startScrolling(elements: QuoteElements, state: QuoteState): void {
    state.isScrolling = true;

    if (state.scrollTimeout !== undefined) {
        window.clearTimeout(state.scrollTimeout);
    }

    state.scrollTimeout = window.setTimeout(() => {
        if (state.isScrolling) {
            showNextQuote(elements, state);
            startScrolling(elements, state);
        }
    }, 5000);
}

function pauseScrolling(state: QuoteState): void {
    state.isScrolling = false;
    if (state.scrollTimeout !== undefined) {
        window.clearTimeout(state.scrollTimeout);
        state.scrollTimeout = undefined;
    }
}

function toggleScrolling(elements: QuoteElements, state: QuoteState): void {
    if (state.isScrolling) {
        pauseScrolling(state);
    } else {
        startScrolling(elements, state);
    }
}

function setupResizeListener(elements: QuoteElements, state: QuoteState): void {
    const resizeObserver = new ResizeObserver(() => {
        setContainerHeight(elements, state);
    });

    resizeObserver.observe(elements.quotesContainer);

    window.addEventListener("resize", () => {
        setContainerHeight(elements, state);
    });
}
